import startCase from 'lodash.startcase';
import { format } from 'date-fns';
import { QuestionTypeEnum } from './enums.js';
const KNOWN_DATE_KEYS = new Set([
    'dateCompleted', 'createdAt', 'updatedAt', 'dateStarted', 'completedAt', 'startedAt',
]);
const MS_MIN = 1_000_000_000_000;
const MS_MAX = 4_000_000_000_000;
const getRawValueType = (v) => {
    if (v == null)
        return 'unknown';
    if (typeof v === 'string')
        return /^\d{4}-\d{2}-\d{2}/.test(v) ? 'date-string' : 'string';
    if (typeof v === 'number')
        return v > MS_MIN && v < MS_MAX ? 'ms-number' : 'number';
    if (typeof v === 'boolean')
        return 'boolean';
    if (Array.isArray(v))
        return 'array';
    if (typeof v['toDate'] === 'function')
        return 'firestore-ts';
    return 'unknown';
};
const detectColumnTypeInfo = (key, samples) => {
    const values = samples.filter((v) => v != null);
    const rawType = values.length ? getRawValueType(values[0]) : 'unknown';
    if (KNOWN_DATE_KEYS.has(key)) {
        return { type: 'date', alternatives: rawType === 'ms-number' ? ['number', 'text'] : ['text'] };
    }
    switch (rawType) {
        case 'date-string': return { type: 'date', alternatives: ['text'] };
        case 'ms-number': return { type: 'date', alternatives: ['number', 'text'] };
        case 'firestore-ts': return { type: 'date', alternatives: ['text'] };
        case 'string': return { type: 'text', alternatives: [] };
        case 'number': return { type: 'number', alternatives: [] };
        case 'boolean': return { type: 'boolean', alternatives: ['text'] };
        case 'array': return { type: 'list', alternatives: ['text'] };
        default: return { type: 'unknown', alternatives: [] };
    }
};
export const coerceValue = (raw, toType) => {
    if (raw == null)
        return raw;
    switch (toType) {
        case 'date': {
            if (typeof raw === 'number' && raw > MS_MIN && raw < MS_MAX)
                return format(new Date(raw), 'MM/dd/yyyy');
            if (typeof raw === 'string') {
                if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
                    const d = new Date(raw);
                    if (!isNaN(d.getTime()))
                        return format(d, 'MM/dd/yyyy');
                }
                return raw;
            }
            if (typeof raw['toDate'] === 'function')
                return format(raw.toDate(), 'MM/dd/yyyy');
            return raw;
        }
        case 'text':
            return Array.isArray(raw) ? raw.join(', ') : String(raw);
        case 'number': {
            if (typeof raw === 'number')
                return raw;
            // Number('') is 0, and a blank severity exported as a zero is a very
            // convincing wrong answer: it reads as "recorded as none" rather than
            // "never answered". Blank stays blank.
            const text = String(raw).trim();
            if (!text)
                return null;
            const value = Number(text);
            return Number.isNaN(value) ? null : value;
        }
        case 'boolean':
            return Boolean(raw);
        case 'list':
            return Array.isArray(raw) ? raw.join(', ') : String(raw);
        default:
            return raw;
    }
};
export const mapAnswerToQuestion = (answer, question) => {
    if (question?.type === QuestionTypeEnum.SELECT_ONE) {
        const options = question.options;
        const option = options?.find((option) => option.id === answer);
        if (option) {
            return option.label;
        }
    }
    else if (question?.type === QuestionTypeEnum.SELECT_MANY) {
        return question.options
            ?.filter((option) => answer?.subAnswers?.some((subAnswer) => subAnswer.id === option.id))
            .map((option) => option.label);
    }
    else if (question?.type === QuestionTypeEnum.LONG_TEXT) {
        return answer;
    }
    else if (question?.type === QuestionTypeEnum.SHORT_TEXT) {
        return answer;
    }
};
/** Count and completion-date span of the rows about to be exported. */
export const summarizeResponses = (rows) => {
    let firstCompleted = null;
    let lastCompleted = null;
    (rows ?? []).forEach((row) => {
        const raw = row?.dateCompleted;
        if (!(typeof raw === 'string' || typeof raw === 'number' || raw instanceof Date))
            return;
        const date = new Date(raw);
        if (isNaN(date.getTime()))
            return;
        if (!firstCompleted || date < firstCompleted)
            firstCompleted = date;
        if (!lastCompleted || date > lastCompleted)
            lastCompleted = date;
    });
    return { count: rows?.length ?? 0, firstCompleted, lastCompleted };
};
const isNestedValue = (value) => value !== null && typeof value === 'object';
// Up to five non-empty values, so a column that's blank on the first few rows
// still gets its type detected from the rows that do have it.
const sampleValues = (responses, read) => responses
    .map(read)
    .filter((value) => value != null)
    .slice(0, 5);
export const buildReportDataColumns = (responses, ignore) => {
    const columns = [];
    if (responses?.length) {
        // Rows don't all carry the same fields — an empty department list on the
        // first row used to drop every department column — so collect keys from
        // every row, in first-seen order.
        const keys = new Set();
        responses.forEach((row) => Object.keys(row).forEach((key) => keys.add(key)));
        keys.forEach((key) => {
            if (key === 'pages') {
                // Skip pages here — per-question columns are built by breakOutAnswersAndSubAnswers
                return;
            }
            else if (responses.some((row) => isNestedValue(row[key]))) {
                const subKeys = new Set();
                responses.forEach((row) => {
                    if (isNestedValue(row[key])) {
                        Object.keys(row[key]).forEach((subKey) => subKeys.add(subKey));
                    }
                });
                subKeys.forEach((subKey) => {
                    if (!ignore.includes(`${key}.${subKey}`)) {
                        const subSamples = sampleValues(responses, (r) => r[key]?.[subKey]);
                        const { type, alternatives } = detectColumnTypeInfo(subKey, subSamples);
                        columns.push({
                            label: `${startCase(key)} - ${startCase(subKey)}`,
                            type,
                            alternatives,
                            rawValue: (row) => row[key]?.[subKey],
                            value: (row) => {
                                const value = row[key]?.[subKey];
                                if (typeof value === 'string' ||
                                    typeof value === 'number' ||
                                    typeof value === 'boolean') {
                                    return value;
                                }
                                else if (value == null) {
                                    return '';
                                }
                                else {
                                    return JSON.stringify(value);
                                }
                            },
                        });
                    }
                });
            }
            else {
                const topSamples = sampleValues(responses, (r) => r[key]);
                const { type, alternatives } = detectColumnTypeInfo(key, topSamples);
                const isDateField = type === 'date';
                columns.push({
                    label: startCase(key),
                    type,
                    alternatives,
                    rawValue: (row) => row[key],
                    value: (row) => {
                        const value = row[key];
                        if (typeof value === 'string') {
                            // ISO / YYYY-MM-DD string
                            if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
                                const d = new Date(value);
                                if (!isNaN(d.getTime()))
                                    return format(d, 'MM/dd/yyyy');
                            }
                            if (isDateField) {
                                // String-encoded ms timestamp e.g. "1705276800000"
                                if (/^\d+$/.test(value)) {
                                    const n = Number(value);
                                    if (n > MS_MIN && n < MS_MAX)
                                        return format(new Date(n), 'MM/dd/yyyy');
                                }
                                // Locale date string e.g. "Mon Jan 15 2024 00:00:00 GMT+0000"
                                // (produced by Apollo Server's String scalar calling String(date))
                                const d = new Date(value);
                                if (!isNaN(d.getTime()))
                                    return format(d, 'MM/dd/yyyy');
                            }
                            return value;
                        }
                        else if (typeof value === 'number') {
                            if (value > MS_MIN && value < MS_MAX) {
                                console.log(`Formatting ms timestamp for key "${key}":`, value, '→', format(new Date(value), 'MM/dd/yyyy'));
                                return format(new Date(value), 'MM/dd/yyyy');
                            }
                            return value;
                        }
                        else if (typeof value === 'boolean') {
                            return value;
                        }
                        else if (value) {
                            return format(value.toDate(), 'MM/dd/yyyy');
                        }
                        else {
                            // Blank beats the literal string "null" in a spreadsheet cell.
                            return '';
                        }
                    },
                });
            }
        });
    }
    return columns;
};
/**
 * Identity for a question that has no id: its text plus its type. Editing a
 * question's wording therefore starts a new column here, which is the honest
 * outcome for an export — the two aren't the same question as far as this
 * file can tell.
 *
 * Exported because the question-id backfill matches on exactly this before it
 * falls back to position. Both must agree on what "the same question" means,
 * or a chart and an export of one survey would disagree.
 */
export const getQuestionKey = (question) => `${question.type}::${(question.question ?? '').trim().toLowerCase()}`;
const buildQuestionPositions = (version) => {
    const positions = new Map();
    version.pages?.forEach((page, pageIndex) => {
        page?.questions?.forEach((question, questionIndex) => {
            const key = getQuestionKey(question);
            if (!positions.has(key)) {
                positions.set(key, { pageIndex, questionIndex, question });
            }
        });
    });
    return positions;
};
/**
 * Builds one column per distinct question across every supplied version,
 * newest version first. Answers are stored positionally, so each row is
 * decoded against the version it was actually submitted on — a question that
 * moved between versions still lands in a single column.
 */
export const breakOutAnswersAndSubAnswers = (_responses, versions) => {
    const columns = [];
    const positionsByVersionId = new Map();
    (versions ?? []).forEach((version) => {
        positionsByVersionId.set(version.id, buildQuestionPositions(version));
    });
    const seen = new Set();
    (versions ?? []).forEach((version) => {
        const positions = positionsByVersionId.get(version.id);
        positions?.forEach((position, key) => {
            if (seen.has(key))
                return;
            seen.add(key);
            const { pageIndex, questionIndex, question } = position;
            const questionLabel = `page${pageIndex + 1}.${questionIndex + 1} - ${startCase(question.question)} - Answer`;
            const resolve = (row) => {
                const positions = positionsByVersionId.get(row?.templateVersionId);
                // Rows from an unknown version fall back to this version's layout,
                // which is what the single-version report did before.
                if (!positions)
                    return position;
                // A known version that never asked this question has no answer for it —
                // falling back here would read whatever else sits at that index.
                return positions.get(key);
            };
            const rawValueFn = (row) => {
                const resolved = resolve(row);
                if (!resolved)
                    return undefined;
                return row?.pages?.[resolved.pageIndex]?.answers?.[resolved.questionIndex];
            };
            // Option ids are version-scoped, so map the answer with that version's question.
            const mappedValueFn = (row) => {
                const resolved = resolve(row);
                if (!resolved)
                    return undefined;
                return mapAnswerToQuestion(rawValueFn(row), resolved.question);
            };
            if (question.type === QuestionTypeEnum.SELECT_ONE) {
                columns.push({
                    label: questionLabel,
                    type: 'text',
                    alternatives: [],
                    rawValue: rawValueFn,
                    value: mappedValueFn,
                });
            }
            else if (question.type === QuestionTypeEnum.SELECT_MANY) {
                columns.push({
                    label: questionLabel,
                    type: 'list',
                    alternatives: ['text'],
                    rawValue: mappedValueFn,
                    value: (row) => JSON.stringify(mappedValueFn(row)),
                });
            }
            else if (question.type === QuestionTypeEnum.LONG_TEXT ||
                question.type === QuestionTypeEnum.SHORT_TEXT) {
                columns.push({
                    label: questionLabel,
                    type: 'text',
                    alternatives: [],
                    rawValue: rawValueFn,
                    value: rawValueFn,
                });
            }
        });
    });
    return columns;
};
